import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Image } from "react-bootstrap";
import EditModal from "./EditModal";
import { FaEdit } from "react-icons/fa";
import { BsTrash } from "react-icons/bs";
import { User, Users } from "../../types/Types";
import { useDeleteUser } from "../../api/mutations/useDeleteUser";
import { useAppContext } from "../../Context/AppContext";
import { useGetUser } from "../../api/queries/useGetUser";
import { useGetBilling } from "../../api/queries/useGetBilling";
import { useDeletePaymentMethod } from "../../api/mutations/useDeletePaymentMethod";
import { getAccessTokenObjectId } from "../../auth/authStorage";
import { IoIosArrowDown } from "react-icons/io";
import { BsUpload } from "react-icons/bs";
import { IoLocationOutline } from "react-icons/io5";
import BusinessProfile from "./BusinessProfile";
import YouthProfile from "./YouthProfile";
import { FaUniversity } from "react-icons/fa";
import UploadImage from "./UploadImage";
import CurvedTextOverlay from "./CurvedTextOverlay";
import { useMediaQuery } from "react-responsive";
import AddPaymentMethodModal from "./AddPaymentMethodModal";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import TranslatableText from "../common/TranslatableText";
import {
  isEarlyStageMembership,
  isLegacyYouthMembership,
  isPaidPortalMember,
} from "../../utils/membershipTier";

const MyAccount: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [removingMethodId, setRemovingMethodId] = useState<string | null>(null);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const { objectId, setObjectId } = useAppContext();
  const tokenObjectId = useMemo(() => getAccessTokenObjectId(), []);
  const effectiveObjectId = tokenObjectId || objectId || "";

  useEffect(() => {
    if (tokenObjectId && tokenObjectId !== objectId) {
      setObjectId(tokenObjectId);
    }
  }, [objectId, setObjectId, tokenObjectId]);

  const { data: userResponse } = useGetUser({
    id: effectiveObjectId,
    enabled: !!effectiveObjectId,
  });
  const { data: paymentMethods, refetch: refetchPaymentMethods } =
    useGetBilling({ userId: effectiveObjectId });
  const userData = { ...userResponse?.user, ...userResponse?.imageUrls };
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });

  const imageSize = isDesktop ? 150 : isLgTablet ? 140 : isMdTablet ? 130 : 95;

  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser({
    onSuccess: () => {
      setShowDeactivateModal(false);
      window.location.reload();
    },
  });

  const { mutate: deletePaymentMethod } = useDeletePaymentMethod({
    onSuccess: () => {
      setRemovingMethodId(null);
      setShowRemoveModal(false);
      refetchPaymentMethods();
    },
  });

  const handleEdit = () => {
    if (userData) setShowEditModal(true);
  };

  const handleDeactivate = () => {
    if (userData.id !== undefined) {
      deleteUser(userData.id);
    }
  };

  const handleConfirmRemove = () => {
    if (!selectedPaymentMethod?.id) return;
    setRemovingMethodId(selectedPaymentMethod.id);
    deletePaymentMethod(selectedPaymentMethod.id);
  };

  const fields = Object.entries(userData || {}).filter(
    ([key, value]) =>
      key.trim() !== "" &&
      value !== null &&
      value !== "" &&
      ![
        "password",
        "objectId",
        "isGDPRConsent",
        "isEmailUpdates",
        "isTermConditions",
        "isDirectory",
        "hasPaid",
        "hasCompletedProfile",
        "id",
        "membershipId",
        "consentToShareProfile",
        "isAdmin",
        "membershipType",
        "discountEndDate",
        "discountCode",
        "membershipName",
        "isAnnualMembership",
        "BusinessLogo",
        "PersonalProfessionalPhoto",
        "businessAddressLineTwo",
        "county",
        "businessAbout",
        "personalAbout",
        "businessProductServices",
        "businessDiscountCode",
        "businessDiscount",
        "ProfessionalPhoto",
        "skillsExperience",
      ].includes(key)
  );

  const totalPossibleFields = 18;

  const filledFieldsCount = fields.length;
  const completionPercent = Math.min(
    Math.round((filledFieldsCount / totalPossibleFields) * 100),
    100
  );
  const accountFullName = useMemo(() => {
    const first =
      (userData.firstName || "").charAt(0).toUpperCase() +
      (userData.firstName || "").slice(1);
    const last =
      (userData.lastName || "").charAt(0).toUpperCase() +
      (userData.lastName || "").slice(1);
    return `${first} ${last}`.trim();
  }, [userData.firstName, userData.lastName]);

  const headerFields = useMemo(
    () => ({
      fullName: accountFullName,
      studyInterest: userData.studyInterest,
      personalPositionCompany: userData.personalPositionCompany,
      city: userData.city,
      country: userData.country,
      universityCollege: userData.universityCollege,
      businessName: userData.businessName,
    }),
    [
      accountFullName,
      userData.businessName,
      userData.city,
      userData.country,
      userData.personalPositionCompany,
      userData.studyInterest,
      userData.universityCollege,
    ]
  );
  const { data: translatedHeader } = useTranslateDynamicFields(
    headerFields,
    i18n.resolvedLanguage || i18n.language
  );
  const tx = (key: keyof typeof headerFields, fallback?: string) =>
    translatedHeader?.[key] || fallback || "";
  const isEarlyStageMember = isEarlyStageMembership(userData?.membershipName);
  const isLegacyYouthMember = isLegacyYouthMembership(userData?.membershipName);
  const showMembershipBadge = isPaidPortalMember(userData);

  return (
    <div className="p-4 pt-5 pt-md-5 m-0">
      <div className="card shadow-lg border-0 pb-2">
        <div>
          <div
            className={`position-relative ${
              userData.BannerImage ? "" : "gradient-bg"
            } profile-banner p-2 rounded-top`}
            style={{
              backgroundImage: `url(${userData.BannerImage || ""})`,
              backgroundSize: "cover", // Show full image without zoom/cropping
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              width: "100%",
              height: "100%",
              aspectRatio: "3 / 1",
            }}
          >
            <div className=" gradient-bg text-white d-flex justify-content-end align-items-center">
              <div className="dropdown position-absolute top-0 end-0 m-2">
                <button
                  type="button"
                  className="btn border-0 bg-transparent text-white d-inline-flex align-items-center justify-content-center p-1 shadow-none"
                  id="accountActionsDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  aria-label={t("account.manageBanner", { defaultValue: "Manage Banner" })}
                >
                  <IoIosArrowDown
                    size={20}
                    aria-hidden
                    style={{
                      filter:
                        "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8)) drop-shadow(0 2px 6px rgba(0, 0, 0, 0.55))",
                    }}
                  />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="accountActionsDropdown"
                >
                  <li>
                    <button className="dropdown-item" onClick={handleEdit}>
                      <FaEdit size={16} className="me-2" /> {t("common.update")}
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => setShowDeactivateModal(true)}
                    >
                      <BsTrash size={16} className="me-2" /> {t("account.deactivate")}
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => setShowBannerModal(true)}
                    >
                      <BsUpload size={16} className="me-2" />{" "}
                      {userData.BannerImage ? t("account.changeBanner") : t("account.addBanner")}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            {!userData.BannerImage && (
              <div className="d-flex h-100 justify-content-center align-items-center">
                <img
                  src="/SMBF-logo-white.png"
                  alt={t("account.alt.brandLogo")}
                  className="w-25 h-25"
                />
              </div>
            )}
            <div className="position-absolute translate-middle image-container">
              <div className="position-relative w-100 h-100">
                <Image
                  src={
                    userData.ProfessionalPhoto ||
                    userData.PersonalProfessionalPhoto ||
                    "/user.png"
                  }
                  roundedCircle
                  alt={t("account.alt.profilePhoto")}
                  className="profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                />

                <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                  {showMembershipBadge && (
                    <CurvedTextOverlay
                      size={imageSize}
                      stroke={
                        isEarlyStageMember
                          ? "#18c2c6"
                          : isLegacyYouthMember
                          ? "#18c2c6"
                          : userData?.membershipName === "Business Membership"
                          ? "#1c57b9"
                          : "#544bc1"
                      }
                      text={
                        isEarlyStageMember
                          ? "#EarlyStage"
                          : isLegacyYouthMember
                          ? "#Youth"
                          : userData?.membershipName === "Business Membership"
                          ? "#Business"
                          : "#Executive"
                      }
                    />
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="mx-3 pt-5 flex-grow-1">
            <div className="d-flex justify-content-between align-items-center account-summary-row">
              <div className="mt-md-4 mt-lg-5 account-summary-primary">
                <h2 className="mb-0">
                  <TranslatableText text={tx("fullName", accountFullName)} />
                </h2>
                <span>
                  {(isLegacyYouthMember || isEarlyStageMember)
                    ? userData.studyInterest &&
                      (
                        <TranslatableText
                          text={tx(
                            "studyInterest",
                            userData.studyInterest.charAt(0).toUpperCase() +
                              userData.studyInterest.slice(1)
                          )}
                        />
                      )
                    : userData.personalPositionCompany &&
                      (
                        <TranslatableText
                          text={tx(
                            "personalPositionCompany",
                            userData.personalPositionCompany.charAt(0).toUpperCase() +
                              userData.personalPositionCompany.slice(1)
                          )}
                        />
                      )}
                </span>
                {(userData.city || userData.country) && (
                  <div className="d-flex gap-1">
                    <IoLocationOutline className="mt-1" />
                    <span className="text-muted">
                      <TranslatableText text={tx("city", userData.city)} />,{" "}
                      <TranslatableText text={tx("country", userData.country)} />
                    </span>
                  </div>
                )}
              </div>
              <div className="d-none d-md-block account-summary-secondary">
                {(isLegacyYouthMember || isEarlyStageMember) ? (
                  <div className="fw-semibold d-flex gap-1 align-items-center">
                    <FaUniversity size={25} />
                    <TranslatableText
                      text={tx("universityCollege", userData.universityCollege)}
                    />
                  </div>
                ) : (
                  <div className="d-flex gap-3 align-items-center cursor-pointer">
                    <Image
                      src={userData.BusinessLogo}
                      className="rounded-circle"
                      width={40}
                      height={40}
                      style={{ objectFit: "contain" }}
                      alt={t("account.alt.brandLogo")}
                    />
                    <div className="fw-semibold">
                      <TranslatableText text={tx("businessName", userData.businessName)} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {completionPercent < 100 && (
        <div className="progress mt-3" style={{ height: "15px" }}>
          <div
            className="progress-bar bg-success"
            role="progressbar"
            style={{ width: `${completionPercent}%` }}
            aria-valuenow={completionPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span className={"text-black-md-white"}>
              {t("account.profileCompleted", { percent: completionPercent })}
            </span>
          </div>
        </div>
      )}
      {(isLegacyYouthMember || isEarlyStageMember)
        ? userData.id && <YouthProfile userData={userData as User} />
        : userData.id && <BusinessProfile userData={userData as User} />}

      {/* Manage Membership */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("membership.manageTitle")}</h5>
        </div>
        <div className="p-3">
          <p className="text-dark small mb-3 fw-bold">
            {t("account.upgradeOrCancelMembership")}
          </p>
          <Button
            variant="outline-primary"
            className="w-100 manage-membership-btn"
            onClick={() => navigate("/manage-membership")}
          >
            {t("membership.manageTitle")}
          </Button>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.paymentMethods")}</h5>
        </div>
        <div className="p-3">
          {paymentMethods && paymentMethods.length > 0 ? (
            <>
              {paymentMethods
                .filter((method: any) => method?.last4)
                .filter(
                  (method: any, index: number, self: any[]) =>
                    index ===
                    self.findIndex(
                      (m) => m.brand === method.brand && m.last4 === method.last4
                    )
                )
                .map((method: any, index: number) => (
                  <div
                    key={method.id ?? index}
                    className="d-flex justify-content-between align-items-center border p-2 rounded mb-2"
                  >
                    <div>
                      <p className="mb-1 fw-bold">
                        {method.brand?.toUpperCase()} •••• {method.last4}
                      </p>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setSelectedPaymentMethod(method);
                        setShowRemoveModal(true);
                      }}
                    >
                      {t("connections.remove")}
                    </Button>
                  </div>
                ))}
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-2 manage-membership-btn"
                onClick={() => setShowAddCardModal(true)}
              >
                {t("account.addPaymentMethod")}
              </Button>
            </>
          ) : (
            <>
              <p className="text-muted small mb-2">{t("account.noSavedPaymentMethods")}</p>
              <Button
                variant="primary"
                size="sm"
                className="manage-membership-btn"
                onClick={() => setShowAddCardModal(true)}
              >
                {t("account.addPaymentMethod")}
              </Button>
            </>
          )}
        </div>
      </div>

      <AddPaymentMethodModal
        show={showAddCardModal}
        onHide={() => setShowAddCardModal(false)}
        userId={effectiveObjectId}
        onSuccess={() => refetchPaymentMethods()}
      />

      {/* Modals */}
      <EditModal
        showEditModal={showEditModal}
        setShowEditModal={() => setShowEditModal(false)}
        userData={userData && "id" in userData ? (userResponse as Users) : null}
        completionPercent={completionPercent}
      />
      <Modal
        centered
        show={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("account.deactivateAccount")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("account.deactivateConfirm")}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeactivateModal(false)}
          >
            {t("connections.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeactivate}
            disabled={isDeleting}
          >
            {isDeleting ? t("account.deactivating") : t("account.deactivate")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showRemoveModal}
        onHide={() => setShowRemoveModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("account.removePaymentMethod")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("account.removePaymentMethodBody", {
            brand: selectedPaymentMethod?.brand?.toUpperCase() ?? "",
            last4: selectedPaymentMethod?.last4 ?? "",
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
            {t("connections.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmRemove}
            disabled={removingMethodId === selectedPaymentMethod?.id}
          >
            {removingMethodId === selectedPaymentMethod?.id
              ? t("feed.removing")
              : t("connections.remove")}
          </Button>
        </Modal.Footer>
      </Modal>
      <UploadImage
        showBannerModal={showBannerModal}
        setShowBannerModal={setShowBannerModal}
        userData={userData as User}
      />
    </div>
  );
};

export default MyAccount;